import { Controller } from "@hotwired/stimulus";

// Fired by Turbo/idiomorph on every element it reconciles during a morph refresh (see
// form-select-controller.ts for the same event on the same class of problem). Listened for here too:
// a refused create post re-renders the same page in place via Turbo morph, so this controller's own
// element survives and never re-runs connect() — without this, the end-date field's shown/hidden and
// required state would go stale against whatever status the refused post actually carried.
const MORPH_ELEMENT_EVENT = "turbo:morph-element";

// A create form's live convenience for "this row is being recorded already in its end state": choosing
// an end state (e.g. Person's Departed) reveals the end-date field and marks it required, and clears
// a start-like date that was pre-filled with today — because a row that ended in the past would
// otherwise fail the start/end ordering guard on the first, most common attempt. The clearing is
// driven by the member's own choice only (see toggle), never by connect or by a morph, so a value they
// typed survives a refused post. An edit form, whose start date is a recorded fact rather than a
// pre-fill, sets clearsStartDate to false and keeps it. Purely client-side:
// the actual refusal for a missing or misplaced end date is the server's EndStateDateGuard /
// RecordedDateOrder, so a submission that reaches the server with JS disabled is still judged
// correctly, just without this live guidance.
export default class extends Controller {
   static override targets = ["state", "startDate", "endDateWrapper", "endDateInput"];
   static override values = { endStates: Array, clearsStartDate: { type: Boolean, default: true } };

   declare readonly stateTarget: HTMLSelectElement;
   declare readonly startDateTarget: HTMLInputElement;
   declare readonly hasStartDateTarget: boolean;
   declare readonly endDateWrapperTarget: HTMLElement;
   declare readonly endDateInputTarget: HTMLInputElement;
   declare readonly endStatesValue: string[];
   declare readonly clearsStartDateValue: boolean;

   private readonly handleMorphElement = (event: Event): void => {
      if (!(event.target instanceof Node) || !this.element.contains(event.target)) return;
      // Deferred one microtask so this runs after form-select-controller's own morph handler has
      // restored the select's value — otherwise this would read the value idiomorph just reset to the
      // first option, not the one the refused post actually carried.
      queueMicrotask(() => this.syncEndDateField());
   };

   connect() {
      this.element.addEventListener(MORPH_ELEMENT_EVENT, this.handleMorphElement);
      // Deferred for the same reason as the morph handler: on first connect, the select's own
      // form-select controller (a descendant, connecting after this outer one) has not yet applied its
      // bound value, so reading it synchronously here would see the browser's default first option.
      queueMicrotask(() => this.syncEndDateField());
   }

   disconnect() {
      this.element.removeEventListener(MORPH_ELEMENT_EVENT, this.handleMorphElement);
   }

   /**
    * The member just picked a state. Only this path clears the start date, and that is the point: a
    * refused post re-renders the same page through a Turbo morph, and clearing on connect/morph too
    * would erase whatever date the member had typed — worst of all on the start/end ordering refusal,
    * which tells them the two dates disagree and then throws one of them away.
    */
   toggle() {
      this.syncEndDateField();

      if (this.clearsStartDateValue && this.isEndState && this.hasStartDateTarget) {
         this.startDateTarget.value = "";
      }
   }

   /** Shown-and-required state of the end-date field, safe to run on connect and on every morph. */
   private syncEndDateField(): void {
      this.endDateWrapperTarget.hidden = !this.isEndState;
      this.endDateInputTarget.required = this.isEndState;
   }

   private get isEndState(): boolean {
      return this.endStatesValue.includes(this.stateTarget.value);
   }
}
